import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Gift, MessageCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * ONE reusable email-capture step that sits in front of every existing
 * "Message Ian / Contact Ian" messaging CTA on the public website.
 *
 * It intercepts clicks on any link whose destination opens the visitor's
 * messaging app (sms: / WhatsApp), shows an optional free-guide email popup,
 * then continues to the ORIGINAL destination with that button's own
 * pre-filled message untouched.
 *
 * Appearance of existing buttons is unchanged — nothing is restyled.
 */

const SESSION_KEY = 'bd_guide_prompted';

/** Portal / authenticated routes are untouched by this flow. */
const PORTAL_PREFIXES = [
  '/auth',
  '/instructor',
  '/student',
  '/parent',
  '/admin',
  '/account-settings',
  '/set-password',
  '/magic-auth',
  '/auth-verify',
  '/unauthorised',
];

const isMessagingHref = (href: string) =>
  href.startsWith('sms:') || href.startsWith('https://wa.me') || href.startsWith('whatsapp:');

const MessageIanEmailCapture: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const pendingHref = useRef<string | null>(null);
  const { pathname } = useLocation();
  const isPortal = PORTAL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );

  const continueToMessage = useCallback(() => {
    const href = pendingHref.current;
    pendingHref.current = null;
    setOpen(false);
    if (!href) return;
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* ignore */
    }
    if (href.startsWith('sms:')) {
      window.location.href = href;
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }, []);

  useEffect(() => {
    if (isPortal) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.dataset.directSms === 'true') return;
      const href = anchor.getAttribute('href') || '';
      if (!isMessagingHref(href)) return;
      try {
        if (sessionStorage.getItem(SESSION_KEY)) return;
      } catch {
        return;
      }
      e.preventDefault();
      pendingHref.current = href;
      setOpen(true);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isPortal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || value.length > 255) {
      toast.error('Please enter a valid email address, or continue without it.');
      return;
    }
    setSaving(true);
    try {
      await Promise.allSettled([
        supabase.functions.invoke('add-getresponse-contact', {
          body: { email: value, first_name: value.split('@')[0] || 'Friend', source: 'Free Guide Popup' },
        }),
        supabase.functions.invoke('send-free-guide', { body: { email: value } }),
      ]);
    } catch {
      /* never block the visitor from messaging Ian */
    } finally {
      setSaving(false);
      setEmail('');
      continueToMessage();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) continueToMessage();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 grid place-items-center">
            <Gift className="h-6 w-6 text-white" />
          </div>
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Free Insider Guide
          </p>
          <DialogTitle className="text-center text-xl font-bold leading-snug">
            Don't Book a Driving Instructor Until You've Read This!
          </DialogTitle>
          <DialogDescription className="text-center">
            Discover the 5 essential things every learner should know before spending hundreds of pounds on driving
            lessons.
            <br />
            <br />
            Enter your email and I'll send it straight over. It's completely optional — you can continue to message me
            either way.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            maxLength={255}
            className="h-11"
          />
          <Button type="submit" disabled={saving} className="w-full h-11 font-semibold">
            {saving ? 'Sending...' : 'Get Your Free Guide →'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={continueToMessage}
            className="w-full h-10 text-sm"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            No thanks, just message Ian
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MessageIanEmailCapture;
